<template>
  <main>
    <div v-if="isLoading" class="loading-animation">
      <BusyIndicator />
    </div>

    <NotificationBanner
      v-else-if="errorMessage"
      :message="errorMessage"
      is-danger
    />

    <template v-else>
      <h1>{{ competition.name }}</h1>

      <h2 v-if="competition.dates">
        {{ formatDate(competition.dates.start) }}

        <template
          v-if="
            competition.dates.start.getTime() !==
            competition.dates.end.getTime()
          "
        >
          –
          {{ formatDate(competition.dates.end) }}
        </template>
      </h2>

      <div v-if="competition.details" class="competition-details">
        <div>{{ $t('competition-standings.association') }}:</div>
        <div>{{ formatValue(competition.details?.associationName) }}</div>

        <div>{{ $t('competition-standings.bowling-alleys') }}:</div>
        <div>
          {{
            formatValue(
              (competition.details?.bowlingAlleyNames ?? []).join(', '),
            )
          }}
        </div>

        <div>{{ $t('competition-standings.supervisor') }}:</div>
        <div>{{ formatValue(competition.details?.supervisor) }}</div>

        <div>{{ $t('competition-standings.last-updated') }}:</div>
        <div>{{ formatDate(competition.dates?.update) }}</div>
      </div>

      <CompetitionStandingsFilter
        :active-filter="activeFilter"
        :active-filter-default-index="activeFilterDefaultIndex"
        :rules="filterByRuleOptions"
        :team-sizes="filterByTeamSizeOptions"
        @select="(value) => (activeFilter = value)"
      />

      <CompetitionStandingsIndividual
        v-if="activeFilter.category === 'individual'"
        :slip="selectedIndividualStandingsSlip"
      />

      <CompetitionStandingsTeam
        v-if="activeFilter.category === 'teams'"
        :slip="selectedTeamStandingsSlip"
      />
    </template>
  </main>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'

import BusyIndicator from '@/components/common/BusyIndicator.vue'
import NotificationBanner from '@/components/common/NotificationBanner.vue'
import CompetitionStandingsFilter, {
  ActiveFilterOpts,
} from '@/components/widgets/public/CompetitionStandingsFilter.vue'
import CompetitionStandingsIndividual from '@/components/widgets/public/CompetitionStandingsIndividual.vue'
import CompetitionStandingsTeam from '@/components/widgets/public/CompetitionStandingsTeam.vue'
import { useCompetitionStore } from '@/store/public/competition'
import {
  CompetitionIndividualStandingsSlip,
  CompetitionTeamStandingsSlip,
} from '@/store/public/competition.types'
import { getErrorMessage } from '@/utils/error-handling'
import { formatDate, formatValue } from '@/utils/format'

const competition = useCompetitionStore()

const errorMessage = ref<string | null>()
const isLoading = ref<boolean>(false)

const activeFilterDefaultIndex = computed<number>((): number =>
  Number((competition.details?.ruleCount ?? 0) > 0),
)

const activeFilter = ref<ActiveFilterOpts>({
  category: 'individual',
  index: 0,
})

onMounted(async (): Promise<void> => {
  isLoading.value = true

  const competitionId = useRoute().params.id as string

  try {
    await competition.fetch(competitionId)
    await competition.fetchIndividualStandings(competitionId)
    await competition.fetchTeamStandings(competitionId)
  } catch (e) {
    const message = getErrorMessage(e as Error)
    console.error(
      `Failed fetching results for competition ID ${competitionId}: ${message}`,
    )
    errorMessage.value = message
  }

  activeFilter.value.index = activeFilterDefaultIndex.value

  isLoading.value = false
})

const filterByRuleOptions = computed<string[]>((): string[] =>
  (competition.standings.individual ?? []).map((slip) => slip.label),
)

const filterByTeamSizeOptions = computed<string[]>((): string[] =>
  (competition.standings.team ?? []).map((slip) => slip.label),
)

const selectedIndividualStandingsSlip =
  computed<CompetitionIndividualStandingsSlip | null>(
    (): CompetitionIndividualStandingsSlip | null =>
      competition.standings.individual?.at(activeFilter.value.index) ?? null,
  )

const selectedTeamStandingsSlip = computed<CompetitionTeamStandingsSlip | null>(
  (): CompetitionTeamStandingsSlip | null =>
    competition.standings.team?.at(activeFilter.value.index) ?? null,
)
</script>

<style lang="scss" scoped>
.loading-animation {
  padding: 2em 0;
  font-size: 3em;
  text-align: center;
}

.competition-details {
  display: grid;
  grid-template-columns: repeat(2, min-content);
  gap: 0.25em 1em;

  div {
    white-space: nowrap;
  }
}
</style>
